import { useCallback, useEffect, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import '../../modules/SettingsDetailPage.css';
import '../../maintenance/maintenance.css';
import { SrCard, SrColumns } from '../../maintenance/srLayout';
import './maintenanceQueue.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm } from '../../../components/confirm';
import { serverMessage } from '../../../components/confirm/serverMessage';
import { useHotkeys } from '../../../components/hotkeys';
import { runLoading } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { ViewToggle } from '../../../components/ViewToggle';
import { fmtLocalDate } from '../../../utils/datetime';
import { fetchDone, rollbackRecord, type DoneView, type RecordTask } from './api';
import MaintenanceAttachments from './MaintenanceAttachments';

// ─────────────────────────────────────────────────────────────────────────────
// Instrument Management ▸ Maintenance Log ▸ Maintained Log ▸ Maintained Record (read-only).
// Port of legacy ALPACA `MaintenanceTaskCheckList_DetailView_MaintainedLog`: the SAME layout as the Maintenance Record,
// every field read-only, with the Task Details of the finished occurrence and the Rollback action
// (`MaintenanceLogRollBack`) that sends the whole occurrence back to the queue with a reason.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  oid: string;
  /** The Maintained Log rows in list order — what the prev / next arrows walk. */
  siblingIds: string[];
  onHome: () => void;
  /** Back to the Maintained Log. */
  onClose: () => void;
  onNavigate: (oid: string) => void;
  /** Write access (MAC): Rollback is greyed without it. */
  canWrite: boolean;
}

const TASK_COLUMNS: AppColumn<RecordTask>[] = [
  { field: 'dueDate', header: 'DueDate', width: 100, type: 'date' },
  { field: 'taskId', header: 'TaskID', width: 80 },
  { field: 'category', header: 'Category', width: 140 },
  { field: 'taskDescription', header: 'Task/Description', width: 220 },
  { field: 'actionDescription', header: 'Action Taken', width: 260 },
  { field: 'comment', header: 'Comment', width: 220 },
  { field: 'completedDate', header: 'Date', width: 130, type: 'datetime' },
  { field: 'completedBy', header: 'CompletedBy', width: 150 },
];

export default function MaintenanceDonePage({ oid, siblingIds, onHome, onClose, onNavigate, canWrite }: Props) {
  const notify = useNotify();
  const { prompt } = useConfirm();
  const [view, setView] = useState<DoneView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'Task Details' | 'Attachment'>('Task Details');
  const at = siblingIds.indexOf(oid);
  const prevOid = at > 0 ? siblingIds[at - 1] ?? null : null;
  const nextOid = at >= 0 && at < siblingIds.length - 1 ? siblingIds[at + 1] ?? null : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await fetchDone(oid));
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Failed to open the maintained record.');
      onClose();
    } finally {
      setLoading(false);
    }
    // onClose / notify are stable enough; reloading on their identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid]);

  useEffect(() => { void load(); }, [load]);

  async function doRollback() {
    if (busy || loading) return;
    // G5 asks for the reason in a small "Rollback Reason" dialog and refuses an empty one.
    const reason = await prompt({ title: 'Rollback Reason', message: 'Please enter the rollback reason.', required: true });
    if (reason === null) return;
    setBusy(true);
    try {
      await runLoading(rollbackRecord(oid, reason), { blocking: true, label: 'Rolling back…' });
      notify.success('Rollback completed successfully for all related tasks.');
      onClose();
    } catch (e) {
      notify.warning(serverMessage(e) ?? 'Rollback failed.');
    } finally { setBusy(false); }
  }

  useHotkeys([
    { combo: 'esc', label: 'Close', group: 'Detail', order: 9, disabled: busy, allowInInput: false, run: onClose },
    { combo: 'alt+left', label: 'Previous record', group: 'Detail', order: 7, disabled: busy || !prevOid, run: () => { if (prevOid) onNavigate(prevOid); } },
    { combo: 'alt+right', label: 'Next record', group: 'Detail', order: 8, disabled: busy || !nextOid, run: () => { if (nextOid) onNavigate(nextOid); } },
  ]);

  const h = view?.header;
  const title = h?.taskDescription ?? '';
  const ro = (label: string, value: string | null | undefined) => (
    <>
      <label>{label}</label>
      <input type="text" readOnly tabIndex={-1} value={value ?? ''} aria-label={label} />
    </>
  );

  return (
    <div className="bl-page mlv-host maint-detail">
      <div className="bl-grid-area">
        <div className="maint-viewtoggle">
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') onClose(); }} />
        </div>
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>Maintained Record – {title}</h2>
              <div className="sd-bc">
                <a onClick={onHome}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>Instrument Management</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose}>Maintenance Log</a>
                <i className="ti ti-chevron-right" />
                <span>{title}</span>
              </div>
            </div>
            <div className="maint-nav">
              <button className="sr-navimg" title="Previous record (Alt+←)" disabled={!prevOid || busy}
                      onClick={() => prevOid && onNavigate(prevOid)}>
                <img src="/nav/sr-nav-back.png" alt="Previous record" />
              </button>
              <button className="sr-navimg sr-navimg-next" title="Next record (Alt+→)" disabled={!nextOid || busy}
                      onClick={() => nextOid && onNavigate(nextOid)}>
                <img src="/nav/sr-nav-fwd.png" alt="Next record" />
              </button>
            </div>
          </div>
          <div className="sd-actionbar">
            <div className="maint-bar-left">
              <button className="ca-btn" onClick={() => void load()} disabled={busy || loading} title="Refresh (Alt+R)">
                <span className="tb-i tb-i-green">🔄</span> Refresh
              </button>
              <button className="ca-btn" onClick={() => void doRollback()} disabled={busy || loading || !canWrite} title="Rollback">
                <span className="tb-i tb-i-red">↩︎</span> Rollback
              </button>
            </div>
            <button className="ca-btn" onClick={onClose} disabled={busy} title="Close (Esc)">
              <span className="tb-i">↩︎</span> Close
            </button>
          </div>

          {h && view && (
            <div className="maint-detail-body">
              <SrCard title="General Information" icon="🔧">
                <SrColumns>
                  <div className="sr-fgrid maint-fg2">
                    {ro('Maintenance Id', h.maintenanceId)}
                    {ro('Instrument Name', h.instrumentName)}
                    {ro('Maintained Date', fmtLocalDate(h.maintainedDate))}
                    {ro('Maintained By', h.maintainedBy)}
                    {ro('Frequency', h.frequency)}
                    {ro('Next Maintain Date', fmtLocalDate(h.nextMaintainDate))}
                  </div>
                  <div className="sr-fgrid maint-fg2">
                    {ro('Task Ownership', h.taskOwnership)}
                    {ro('Service Company', h.serviceCompany)}
                    {ro('Serviced By', '')}
                    <label htmlFor="md-skip">Skip</label>
                    <input id="md-skip" type="checkbox" checked={h.skip} disabled style={{ width: 'auto', justifySelf: 'start' }} />
                    {h.skip && ro('Skip Reason', h.skipReason)}
                    {ro('Status', h.status)}
                  </div>
                </SrColumns>
              </SrCard>

              <section className="sr-sec">
                <div className="sr-tabbar" role="tablist">
                  {(['Task Details', 'Attachment'] as const).map((t) => (
                    <button key={t} type="button" role="tab" aria-selected={tab === t}
                            className={`sr-tb${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
                  ))}
                </div>
                <div className="sr-sec-b">
                  <div style={tab === 'Task Details' ? undefined : { display: 'none' }}>
                    <AppDataGrid<RecordTask> rows={view.tasks} columns={TASK_COLUMNS} getRowId={(r) => r.oid}
                      loading={loading} defaultPageSize={20} exportFilename="maintained-task-details" emptyText="No tasks." />
                  </div>
                  <div style={tab === 'Attachment' ? undefined : { display: 'none' }}>
                    <MaintenanceAttachments taskOid={oid} initial={view.attachments} canWrite={canWrite}
                      loading={loading} exportName="maintained-attachments" />
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
